import { cn } from '../../lib/cn'
import { SectionTitle } from '../SectionTitle/SectionTitle'

export interface NumberedGridProps {
  title: string
  items: string[]
  onItemClick?: (index: number) => void
  disclaimer?: string
  className?: string
}

export const NumberedGrid = ({
  title,
  items,
  onItemClick,
  disclaimer,
  className,
}: NumberedGridProps) => {
  const fillerCount = (4 - (items.length % 4)) % 4

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <SectionTitle title={title} />
      <ol className='list-none m-0 p-0 flex flex-col md:grid md:grid-cols-4 md:gap-px md:bg-edu-stroke-medium md:border md:border-edu-stroke-medium'>
        {items.map((itemTitle, index) => {
          const number = index + 1
          const displayNum = String(number).padStart(2, '0')

          const innerContent = (
            <>
              <span
                className='font-bold text-body-1 leading-none shrink-0 md:hidden'
                aria-hidden='true'
              >
                {number}.
              </span>
              <span
                className='hidden font-bold leading-none shrink-0 md:text-title-3 md:flex md:items-center md:justify-center md:w-12 md:h-12 md:border md:border-white/25'
                aria-hidden='true'
              >
                {displayNum}
              </span>
              <span className='text-[20px] leading-body-1 tracking-[-0.01em]'>
                {itemTitle}
              </span>
            </>
          )

          return (
            <li
              key={index}
              className={cn(
                'border-t border-edu-stroke-default first:border-t-0 md:border-none md:bg-edu-bg-secondary md:flex md:flex-col md:transition-colors md:duration-200',
                onItemClick &&
                  'md:hover:bg-edu-bg-tertiary md:relative md:hover:z-10 md:hover:shadow-[0_0_0_1px_var(--color-edu-stroke-hover)]',
              )}
            >
              {onItemClick ? (
                <button
                  type='button'
                  aria-label={`${displayNum}: ${itemTitle}`}
                  className='bg-transparent border-none p-0 w-full text-left cursor-pointer text-white flex items-center gap-5 py-3 md:flex-1 md:flex-col md:items-start md:gap-4 md:min-h-64 md:p-5'
                  onClick={() => onItemClick(index)}
                >
                  {innerContent}
                </button>
              ) : (
                <div className='w-full text-white flex items-center gap-4 py-3 md:flex-1 md:flex-col md:items-start md:gap-4 md:min-h-64 md:p-5'>
                  {innerContent}
                </div>
              )}
            </li>
          )
        })}
        {Array.from({ length: fillerCount }).map((_, i) => (
          <li
            key={`filler-${i}`}
            aria-hidden='true'
            className='hidden md:block md:bg-black md:-mr-px md:-mb-px'
          />
        ))}
      </ol>
      {disclaimer && <p className='m-0 text-xs text-white/60'>{disclaimer}</p>}
    </div>
  )
}
